import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { NumberLineQuestionOptions, NumberLineSpec, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { numberLinePrompts as en } from "../Files/Prompts/en/numberLine.ts";
import { numberLinePrompts as es } from "../Files/Prompts/es/numberLine.ts";
import { numberLinePrompts as fr } from "../Files/Prompts/fr/numberLine.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * Number line problems. See ADR 0033.
 *
 * This decides *what* line to draw - the range, how many intervals, which
 * ticks carry labels, and which tick the marker sits on. It does not decide
 * pixels; `Shared/Pdf/NumberLine.tsx` does that, and keeping the two apart is
 * what lets rounding and elapsed time use the drawing later without inheriting
 * anything about worksheets.
 *
 * Every question is the same one - what number is the marker on? - asked in
 * three number systems. The inverse, "mark 3/4 on this line", is not here and
 * will not be: an answer that is a mark on a line is a drawing, which
 * ADR 0022 §1 test 3 rules out (ADR 0033 §3).
 */

/** Denominators a fraction line is divided into. Halves are too coarse to mark. */
const FRACTION_DENOMINATORS = [3, 4, 5, 6, 8, 10];

/** Intervals a whole-number line may use, largest first so the coarsest fits. */
const WHOLE_INTERVALS = [20, 10, 5, 4];

function questionTypes(question: NumberLineQuestionOptions): NumberLineQuestionOptions[] {
    if (question === NumberLineQuestionOptions.Mixed) {
        return [
            NumberLineQuestionOptions.WholeNumbers,
            NumberLineQuestionOptions.Integers,
            NumberLineQuestionOptions.Fractions,
        ];
    }
    return [question];
}

function usableRange(range: number): number {
    return Math.max(4, Math.floor(range) || 20);
}

/**
 * How often to write a label.
 *
 * Twenty labelled ticks on one line is unreadable at print size, so a long
 * line labels every fifth or every second. The marker still sits on a tick
 * either way - reading an unlabelled tick by counting from a labelled one is
 * the skill, not an accident.
 */
function labelEveryFor(intervals: number): number {
    if (intervals > 15) {
        return 5;
    }
    if (intervals > 8) {
        return 2;
    }
    return 1;
}

/**
 * A whole-number line from zero, divided so the ticks land on whole numbers.
 *
 * The interval count is drawn from every candidate that divides the range,
 * rather than taken as the first: `find` always returned the same one, so a
 * whole sheet came out as eight visually identical lines differing only in
 * where the marker sat.
 */
function wholeNumberSpec(range: number): NumberLineSpec {
    const candidates = WHOLE_INTERVALS.filter(
        (candidate) => range % candidate === 0 && candidate <= range
    );
    const intervals =
        candidates.length > 0
            ? candidates[getRandomInt(0, candidates.length - 1)]
            : Math.min(10, range);
    return {
        from: 0,
        to: range,
        intervals,
        labelEvery: labelEveryFor(intervals),
        markAt: (range / intervals) * getRandomInt(1, intervals - 1),
    };
}

/**
 * An integer line, symmetric about zero.
 *
 * The range is halved so the line is not twice as long as a whole-number one
 * at the same setting, and the marker avoids zero: "what number is the marker
 * on" answered by zero is a question about the middle of the line rather than
 * about reading it.
 */
function integerSpec(range: number): NumberLineSpec {
    const half = Math.max(2, Math.round(range / 2));
    const intervals = half * 2;
    let step = getRandomInt(1, intervals - 1);
    if (valueAtStep(half, step) === 0) {
        step += 1;
    }
    return {
        from: -half,
        to: half,
        intervals,
        labelEvery: labelEveryFor(intervals),
        markAt: valueAtStep(half, step),
    };
}

/** The value a step lands on for an integer line. */
function valueAtStep(half: number, step: number): number {
    return -half + step;
}

/** A fraction line from 0 to 1, divided into a denominator's worth of parts. */
function fractionSpec(): NumberLineSpec {
    const denominator = FRACTION_DENOMINATORS[getRandomInt(0, FRACTION_DENOMINATORS.length - 1)];
    const numerator = getRandomInt(1, denominator - 1);
    return {
        from: 0,
        to: 1,
        intervals: denominator,
        labelEvery: 1,
        markAt: numerator / denominator,
        denominator,
    };
}

/** How the marked value is written on the answer key. */
function answerFor(spec: NumberLineSpec): string {
    if (spec.denominator) {
        return `${Math.round(spec.markAt * spec.denominator)}/${spec.denominator}`;
    }
    return String(spec.markAt);
}

/**
 * One number line problem. Always succeeds.
 *
 * The spec rides on its own `ProblemData` field rather than in `numbers`,
 * which is the rule the plans README states and which matters more here than
 * usual: `numbers[3]` is the long division remainder, and a drawing has more
 * than three values to carry anyway.
 */
export function buildNumberLineProblem(
    question: NumberLineQuestionOptions,
    range: number,
    key: number,
    language: PrintedLanguage = 'en'
): ProblemData {
    const usable = usableRange(range);
    let spec: NumberLineSpec;

    switch (question) {
        case NumberLineQuestionOptions.Integers:
            spec = integerSpec(usable);
            break;
        case NumberLineQuestionOptions.Fractions:
            spec = fractionSpec();
            break;
        default:
            spec = wholeNumberSpec(usable);
            break;
    }

    return {
        key,
        numbers: [],
        operator: "",
        display: [PROMPTS[language].arrow(), answerFor(spec)],
        numberLine: spec,
        numberLineQuestion: question,
    };
}

/** A page-shaped set of number line problems. */
export function generateNumberLinePages(settings: ISettingsProps): ProblemData[][] | null {
    const types = questionTypes(settings.NumberLineQuestion ?? NumberLineQuestionOptions.Mixed);
    const range = settings.NumberLineRange ?? 20;
    const language = printedLanguageOf(settings);
    return generateProblemPages({
        settings,
        choices: types,
        build: (question, key) => buildNumberLineProblem(question, range, key, language),
        // Two lines with the same range, spacing and marker are the same
        // problem however the question type got there.
        fingerprint: (problem) => {
            const spec = problem.numberLine!;
            return `${spec.from}:${spec.to}:${spec.intervals}:${spec.markAt}`;
        },
    });
}
